import { useCallback, useEffect, useState } from "react";
import type { ClientSafeLaunchCertificateViewV1 } from "@enrein/shared-contracts/contracts/launch-certificate";
import { toClientApiErrorMessage } from "@enrein/shared-contracts/display/tier1-client-display";
import type { ClientPortalApiClient } from "./api.js";
import { isClientEvidenceNotReadyError } from "./client-evidence-not-ready.js";

export function LaunchCertificateSummaryPanel({
  api,
  clientLinkId,
  token,
}: {
  readonly api: ClientPortalApiClient;
  readonly clientLinkId: string;
  readonly token: string;
}) {
  const [view, setView] = useState<ClientSafeLaunchCertificateViewV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setView(await api.getCurrentClientLaunchCertificate(clientLinkId, token));
    } catch (err) {
      setView(null);
      const message = err instanceof Error ? err.message : String(err);
      if (!isClientEvidenceNotReadyError(message)) {
        setError(toClientApiErrorMessage(message));
      }
    } finally {
      setBusy(false);
    }
  }, [api, clientLinkId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  if (busy && !view) return <p className="muted">Loading launch certificate…</p>;
  if (error) return <p className="error">{error}</p>;
  if (!view) return null;

  return (
    <article className="value-card">
      <p className="value-card-eyebrow">Launch Certificate</p>
      <h3 className="value-card-headline">{view.title}</h3>
      <p>{view.headline}</p>
      {view.warningBanner ? <p className="value-card-warning">{view.warningBanner}</p> : null}
      <p className="muted">{view.aggregateStatusLabel}</p>
      <details>
        <summary>Evidence summary disclaimers</summary>
        <ul>
          {view.disclaimers.slice(0, 3).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </details>
    </article>
  );
}
